import React, { forwardRef, useMemo, useState } from "react";
import styles from './_image.module.scss';

/**
 * @typedef {Object} ImageProps
 * @property {Function} [onLoadCallback]
 * @property {React.ImgHTMLAttributes<HTMLImageElement>} [rest]
 */

/**
 * @description Image extends Next JS' built-in Image component
 * 
 * @component CustomImage
 * 
 * @param {React.ImgHTMLAttributes<HTMLImageElement> & ImageProps} props Image props
 * @param {string} props.src Image source
 * @param {string} [props.alt] Alternative text for the image
 * @param {string} [props.className] Additional class names for styling
 * @param {string} [props.loading] Loading behavior ('lazy' or 'eager')
 * @param {Function} [props.onLoad] Callback function extension on image load
 * @param {number} props.width Width of the image
 * @param {number} props.height Height of the image
 * @param {React.ImgHTMLAttributes<HTMLImageElement>} [props.rest] Additional props 
 * @param {React.RefObject<HTMLImageElement>} ref
 * @returns {HTMLImageElement} Rendered Image component
 */
const CustomImage = forwardRef(function CustomImage(
/** @type {React.ImgHTMLAttributes<HTMLImageElement> & ImageProps & {src: string, width: number, height: number}} */
{
  src,
  width,
  height,
  loading = 'lazy',
  className = undefined,
  onLoad = undefined,
  alt = '',
  ...rest
},
  ref
) {
  const [loaded, setLoaded] = useState(false);

  const containerClass = useMemo(() => {
    let tempClassName = `${styles.image} image`;
    if (styles.image && !loaded) {
      tempClassName = `${tempClassName} ${styles.image__loading} image--loading`;
    }
    if (className) {
      tempClassName = `${tempClassName} ${className}`;
    }

    if (loaded) {
      tempClassName = `${tempClassName} ${styles.image__loaded} image--loaded`;
    }

    return tempClassName;
  }, [loaded, className]);

  return (
    <img
      ref={ref}
      className={containerClass}
      loading={loading}
      alt={alt}
      src={src}
      width={width}
      height={height}
      onLoad={(e) => {
        setLoaded(true);
        onLoad && onLoad(e);
      }}
      {...rest}
    />
  );
});

export default CustomImage;